// Phase P.32 — Compact preview popup for a manager Jobs-page row.
//
// Shows contact / email / phone / prominent total-incl-VAT, plus two
// CTAs: "Send email" (invoice send) and "See details" (deep-link to
// the full BookingDetail via /calendar). Esc + click-outside dismiss
// via the underlying Radix Dialog. Does NOT replace row navigation —
// callers open this INSTEAD of navigating on row click and can still
// reach the full view via "See details".

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, User, Send, ExternalLink, Loader2 } from "lucide-react";

const fmtIncl = (b, currency) => {
  const n = Number(
    b.total_incl_vat != null ? b.total_incl_vat : b.subtotal || 0,
  );
  return formatCurrency(n, currency || "EUR");
};

export default function JobPreviewDialog({ booking, currency, open, onOpenChange }) {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Fetch fresh customer (email/phone) when the popup opens. Keeps the
  // row's cached snapshot honest without slowing the list view.
  useEffect(() => {
    if (!open || !booking?.customer_id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/customers/${booking.customer_id}`)
      .then((r) => { if (!cancelled) setCustomer(r.data || {}); })
      .catch(() => { if (!cancelled) setCustomer({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, booking?.customer_id]);

  if (!booking) return null;

  const emails = (customer?.emails || []).map((e) => e?.email).filter(Boolean);
  const primaryEmail = emails[0] || "";
  const phone = (customer?.phones || [])[0] || booking.customer_phone || "";
  const contactName = customer?.contact_name || booking.customer_contact || "";

  const sendEmail = async () => {
    if (!booking.linked_invoice_id) {
      toast.error("No invoice yet — complete or issue the invoice first.");
      return;
    }
    if (!primaryEmail) {
      toast.error("No customer email on file. Add one on the Customer page.");
      return;
    }
    setSending(true);
    try {
      await api.post(`/invoices/${booking.linked_invoice_id}/send`, {
        recipient_emails: [primaryEmail],
        include_accounts_email: false,
      });
      toast.success(`Invoice emailed to ${primaryEmail}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const goDetails = () => {
    onOpenChange(false);
    navigate(`/calendar?bookingId=${encodeURIComponent(booking.id)}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="job-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <span className="mono text-xs uppercase tracking-widest text-muted-foreground">
              {booking.booking_code}
            </span>
            <span className="truncate">{booking.customer_name}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Quick preview of this booking. Send the invoice or open the full view.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Prominent VAT-inclusive total */}
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3"
            data-testid="job-preview-total-block"
          >
            <div className="mono text-[10px] uppercase tracking-widest text-emerald-800/70">
              Total (incl. VAT)
            </div>
            <div
              className="text-3xl font-black tabular-nums text-emerald-900 mono mt-1"
              data-testid="job-preview-total"
            >
              {fmtIncl(booking, currency)}
            </div>
          </div>

          {/* Contact block */}
          <div className="space-y-2 text-sm">
            {loading && (
              <div className="text-muted-foreground text-xs flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading customer…
              </div>
            )}
            <div className="flex items-center gap-2" data-testid="job-preview-contact-name">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="truncate">{contactName || <span className="text-muted-foreground">No contact name</span>}</span>
            </div>
            <div className="flex items-center gap-2" data-testid="job-preview-contact-email">
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
              {primaryEmail ? (
                <a href={`mailto:${primaryEmail}`} className="text-sky-700 hover:underline truncate">
                  {primaryEmail}
                </a>
              ) : (
                <span className="text-muted-foreground">No email on file</span>
              )}
            </div>
            <div className="flex items-center gap-2" data-testid="job-preview-contact-phone">
              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
              {phone ? (
                <a href={`tel:${phone}`} className="text-sky-700 hover:underline">{phone}</a>
              ) : (
                <span className="text-muted-foreground">No phone on file</span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={sendEmail}
            disabled={sending || !booking.linked_invoice_id || !primaryEmail}
            className="gap-1.5"
            data-testid="job-preview-send-email"
          >
            <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send email"}
          </Button>
          <Button
            type="button"
            onClick={goDetails}
            className="gap-1.5 btn-cta"
            data-testid="job-preview-see-details"
          >
            <ExternalLink className="h-3.5 w-3.5" /> See details
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
